/**
 * ActivityPanel - Painel de atividade estilo timeline para diligências.
 *
 * Exibe o histórico como bullet-points para sistema e cards para mensagens de usuário.
 * Inspirado no estilo ClickSign.
 */

import React, { useRef, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MessageCircle, Paperclip, Reply, FileText, Download } from 'lucide-react';
import { useDiligenciaMessages, type DiligenciaMessage } from '@/features/diligencias/hooks';
import { ChatAvatar } from './chat/ChatAvatar';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  ALLOWED_MIME_TYPES,
  ALLOWED_EXTENSIONS,
  formatFileSize,
  sanitizeFileName,
  validateFile,
} from '@/lib/upload';

// =============================================================================
// TIPOS
// =============================================================================

export interface ActivityPanelProps {
  diligenciaId: string;
  currentUserId?: string;
}

// Tempo de vida do anexo (24h).
const ATTACHMENT_TTL_MS = 24 * 60 * 60 * 1000;
const CHAT_ATTACHMENT_MAX_MB = 10;

// =============================================================================
// HELPERS
// =============================================================================

function formatActivityDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }) + ' às ' + date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeShort(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Agora';
  if (diffMin < 60) return `Há ${diffMin} minutos`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `Há ${diffHours} horas`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Ontem';
  return `Há ${diffDays} dias`;
}

function isExpired(expiresAt: string | undefined): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() <= Date.now();
}

// =============================================================================
// SUB-COMPONENTES
// =============================================================================

// Realça palavras-chave (APROVADA/REPROVADO) renderizando nós React em vez de
// injetar HTML — evita XSS via conteúdo de `message.message`.
const HIGHLIGHT_RE = /(APROVAD[AO]|REPROVAD[AO])/g;

function renderHighlightedMessage(text: string): React.ReactNode[] {
  // split com grupo de captura mantém os tokens casados no array resultante.
  return text.split(HIGHLIGHT_RE).map((part, index) => {
    if (/^APROVAD[AO]$/.test(part)) {
      return (
        <strong key={index} className="text-blue-600">
          {part}
        </strong>
      );
    }
    if (/^REPROVAD[AO]$/.test(part)) {
      return (
        <strong key={index} className="text-red-600">
          {part}
        </strong>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

/** Entrada de sistema — bullet point com texto */
function SystemEntry({ message }: { message: DiligenciaMessage }) {
  return (
    <li className="flex gap-3 text-sm leading-relaxed">
      <span className="mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
      <div>
        <p className="text-muted-foreground">
          {renderHighlightedMessage(message.message)}
        </p>
        <p className="text-[11px] text-muted-foreground/50 mt-0.5">
          {formatActivityDate(message.created_at)}
        </p>
      </div>
    </li>
  );
}

/** Entrada de usuário — card com avatar, nome, mensagem e ação de responder */
function UserEntry({
  message,
  onReply,
}: {
  message: DiligenciaMessage;
  onReply: (senderName: string, text: string) => void;
}) {
  return (
    <li className="flex gap-3">
      <div className="shrink-0 mt-1">
        <ChatAvatar
          name={message.sender_name}
          avatarUrl={message.sender_avatar_url}
          size="sm"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-muted/60 rounded-lg border p-3">
          {/* Header: nome + data */}
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-sm font-semibold text-foreground">
              {message.sender_name || 'Usuário'}
            </span>
            <span className="text-[10px] text-muted-foreground/60">
              {formatActivityDate(message.created_at)}
            </span>
          </div>

          {/* Conteúdo */}
          <p className="text-sm text-foreground/80 leading-relaxed">
            {message.message}
          </p>

          {/* Responder */}
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/50">
            <button
              className="flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-foreground transition-colors font-medium"
              onClick={() => onReply(message.sender_name || 'Usuário', message.message)}
            >
              <Reply className="h-3 w-3" />
              Responder
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

/** Entrada de documento — card com ícone, nome, tamanho, download e aviso de expiração */
function DocumentEntry({ message }: { message: DiligenciaMessage }) {
  const { toast } = useToast();
  const [opening, setOpening] = useState(false);

  const filePath = typeof message.metadata?.file_path === 'string' ? message.metadata.file_path : null;
  const fileName = typeof message.metadata?.file_name === 'string'
    ? message.metadata.file_name
    : message.message;
  const fileSize = typeof message.metadata?.file_size === 'number' ? message.metadata.file_size : null;
  const expiresAt = typeof message.metadata?.expires_at === 'string' ? message.metadata.expires_at : undefined;
  const expired = isExpired(expiresAt);

  async function handleOpen() {
    if (!filePath || expired || opening) return;
    setOpening(true);
    try {
      const { data, error } = await supabase.storage
        .from('chat-attachments')
        .createSignedUrl(filePath, 60 * 5); // 5 minutos

      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[ActivityPanel] signed url error:', err);
      toast({
        title: 'Não foi possível abrir o arquivo',
        description: 'O anexo pode ter expirado ou sido removido.',
        variant: 'destructive',
      });
    } finally {
      setOpening(false);
    }
  }

  return (
    <li className="flex gap-3">
      <div className="shrink-0 mt-1">
        <ChatAvatar
          name={message.sender_name}
          avatarUrl={message.sender_avatar_url}
          size="sm"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-muted/60 rounded-lg border p-3">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-sm font-semibold text-foreground">
              {message.sender_name || 'Usuário'}
            </span>
            <span className="text-[10px] text-muted-foreground/60">
              {formatActivityDate(message.created_at)}
            </span>
          </div>

          <button
            type="button"
            onClick={handleOpen}
            disabled={!filePath || expired || opening}
            className="flex items-center gap-3 w-full text-left bg-background border rounded-md px-3 py-2 hover:border-blue-500/60 hover:bg-blue-500/5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <div className="shrink-0 h-9 w-9 rounded-md bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <FileText className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
              <p className="text-[11px] text-muted-foreground truncate">
                {expired
                  ? 'Arquivo expirado — removido após 24h'
                  : [
                      fileSize ? formatFileSize(fileSize) : null,
                      expiresAt ? `expira ${formatRelativeShort(expiresAt).toLowerCase()}` : null,
                    ]
                      .filter(Boolean)
                      .join(' • ') || 'Anexo'}
              </p>
            </div>
            {!expired && filePath && (
              <Download className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
          </button>
        </div>
      </div>
    </li>
  );
}

// =============================================================================
// INPUT DO CHAT
// =============================================================================

interface ReplyContext {
  senderName: string;
  text: string;
}

function ActivityInput({
  onSend,
  onAttach,
  isSending,
  isUploading,
  replyTo,
  onClearReply,
  inputRef,
}: {
  onSend: (text: string) => void;
  onAttach: (file: File) => void;
  isSending: boolean;
  isUploading: boolean;
  replyTo: ReplyContext | null;
  onClearReply: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const [value, setValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleSend() {
    const text = value.trim();
    if (!text || isSending) return;
    const prefix = replyTo ? `Em resposta a ${replyTo.senderName}: ` : '';
    onSend(prefix + text);
    setValue('');
    onClearReply();
    inputRef.current?.focus();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset para permitir escolher o mesmo arquivo novamente depois.
    e.target.value = '';
    if (file) onAttach(file);
  }

  const busy = isSending || isUploading;

  return (
    <div className="shrink-0 border-t px-4 py-3">
      {/* Reply preview */}
      {replyTo && (
        <div className="flex items-center justify-between bg-muted/50 rounded-t-lg px-3 py-1.5 mb-1 text-xs text-muted-foreground border border-b-0">
          <span className="truncate">
            <Reply className="h-3 w-3 inline mr-1" />
            Respondendo a <span className="font-medium text-foreground">{replyTo.senderName}</span>
          </span>
          <button
            onClick={onClearReply}
            className="text-muted-foreground hover:text-foreground ml-2 shrink-0"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={
            isUploading
              ? 'Enviando anexo…'
              : 'Tire dúvidas, pergunte ou responda a uma solicitação'
          }
          disabled={busy}
          className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground/50 disabled:opacity-60"
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ALLOWED_EXTENSIONS.all.join(',')}
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          aria-label="Anexar documento (expira em 24h)"
          title="Anexar documento (expira em 24h)"
          className="text-muted-foreground/70 hover:text-foreground transition-colors p-1 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Paperclip className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export function ActivityPanel({ diligenciaId, currentUserId: _currentUserId }: ActivityPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [replyTo, setReplyTo] = useState<ReplyContext | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const { messages, isLoading, isSending, sendMessage } =
    useDiligenciaMessages(diligenciaId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function handleReply(senderName: string, text: string) {
    setReplyTo({ senderName, text });
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  async function handleAttach(file: File) {
    if (!diligenciaId || isUploading) return;

    const validation = validateFile(file, {
      maxSizeMB: CHAT_ATTACHMENT_MAX_MB,
      allowedTypes: [...ALLOWED_MIME_TYPES.all],
      allowedExtensions: [...ALLOWED_EXTENSIONS.all],
    });
    if (!validation.valid) {
      toast({
        title: 'Arquivo inválido',
        description: validation.error,
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const timestamp = Date.now();
      const sanitized = sanitizeFileName(file.name);
      const filePath = `${diligenciaId}/${timestamp}_${sanitized}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-attachments')
        .upload(filePath, file, {
          contentType: file.type || undefined,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const expiresAt = new Date(Date.now() + ATTACHMENT_TTL_MS).toISOString();

      const ok = await sendMessage(file.name, 'document', {
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        expires_at: expiresAt,
      });

      if (!ok) {
        // Insert falhou → tenta limpar o arquivo órfão.
        await supabase.storage.from('chat-attachments').remove([filePath]).catch(() => {});
        return;
      }
    } catch (err) {
      console.error('[ActivityPanel] attach error:', err);
      toast({
        title: 'Falha ao enviar anexo',
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Card className="flex flex-col h-full min-h-0 lg:min-h-[600px]">
      {/* Header */}
      <CardHeader className="shrink-0 py-4">
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-sm font-bold uppercase tracking-wide text-foreground">
            Atividade
          </CardTitle>
        </div>
        <p className="text-[11px] text-muted-foreground/60 mt-1">
          Histórico de atividades e de conversas
        </p>
      </CardHeader>

      {/* Timeline */}
      <CardContent className="flex flex-col flex-1 min-h-0 p-0">
        <div
          role="log"
          aria-live="polite"
          className="flex-1 overflow-y-auto px-5 py-3 min-h-0 max-h-[calc(100vh-320px)]"
        >
          {isLoading && (
            <div className="flex flex-1 items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && messages.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
              <MessageCircle className="w-8 h-8 opacity-40" />
              <p className="text-sm">Nenhuma atividade ainda</p>
            </div>
          )}

          {!isLoading && messages.length > 0 && (
            <ul className="space-y-4">
              {messages.map((msg) => {
                if (msg.type === 'system') {
                  return <SystemEntry key={msg.id} message={msg} />;
                }
                if (msg.type === 'document') {
                  return <DocumentEntry key={msg.id} message={msg} />;
                }
                return <UserEntry key={msg.id} message={msg} onReply={handleReply} />;
              })}
            </ul>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <ActivityInput
          onSend={sendMessage}
          onAttach={handleAttach}
          isSending={isSending}
          isUploading={isUploading}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          inputRef={inputRef}
        />
      </CardContent>
    </Card>
  );
}

export default ActivityPanel;
